import { useMemo, useCallback } from "react";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import Icon from "../icon";
import classNames from "classnames";
import capitalize from "../../utils/capitalize";
import { getTranslatedField } from "../../utils/lang";

dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);

const StageCard = ({
  stage,
  appLang,
  labels,
  multiple,
  setEventNavigation,
  register,
  selectedStage,
  setValue,
  trigger,
  setFocus,
}) => {
  const months = useMemo(() => {
    dayjs.locale(appLang?.value);
    return dayjs.monthsShort();
  }, [appLang?.value]);

  const stageInfo = useMemo(() => {
    const start = dayjs(Number(stage?.stageStart));
    const end = dayjs(Number(stage?.stageEnd));
    const multipleDaysStage = !start.isSame(end, "day");
    const multipleMonthsStage = !start.isSame(end, "month");

    return {
      month: multipleMonthsStage
        ? [months[start.month()], months[end.month()]]
        : months[start.month()],
      date: multipleDaysStage ? [start.date(), end.date()] : start.date(),
      dateStart: start.locale(appLang?.value).format("L"),
      dateEnd: end.locale(appLang?.value).format("L"),
      timeStart: dayjs(Number(stage?.stageStart)).locale(appLang?.value).format("LT"),
      timeEnd: dayjs(Number(stage?.stageEnd)).locale(appLang?.value).format("LT"),
      location:
        stage?.location?.location &&
        `${stage?.location?.location} - ${stage?.location?.address} ${stage?.location?.nr}, ${stage?.location?.cap} ${stage?.location?.city}`,
    };
  }, [stage, appLang, months]);

  const handleGoToStageDetail = useCallback(
    (e) => {
      e.stopPropagation();
      setEventNavigation({
        url: "stage-detail",
        id: stage?.id,
      });
    },
    [setEventNavigation, stage]
  );

  const onChange = useCallback(() => {
    if (!!setEventNavigation) {
      if (multiple) {
        setValue("stage", `${stage?.id}`);
        trigger("stage");
        setFocus("email");
      } else {
        handleGoToStageDetail();
      }
    }
  }, [multiple, handleGoToStageDetail, setEventNavigation, setValue, stage, trigger, setFocus]);

  return (
    <div
      id={stage?.id}
      data-id={stage?.id}
      className={classNames(
        "event__stage",
        Number(selectedStage) === stage?.id && multiple && "event__stage--selected",
        !setEventNavigation && "event__stage--read-only",
        Array.isArray(stageInfo.date) && "event__stage--multiple",
        !multiple && "event__stage--disabled"
      )}
      onClick={multiple ? onChange : () => null}
    >
      <div className={classNames("event__stage-calendar")}>
        <span className="event__stage-month">
          {Array.isArray(stageInfo?.month)
            ? `${capitalize(stageInfo.month[0])} - ${capitalize(stageInfo.month[1])}`
            : `${capitalize(stageInfo.month)}`}
        </span>
        <span className={"event__stage-day"}>
          {Array.isArray(stageInfo.date) ? (
            <>
              <span>{stageInfo.date[0]}</span>
              <span>-</span>
              <span>{stageInfo.date[1]}</span>
            </>
          ) : (
            stageInfo.date
          )}
        </span>
      </div>
      <div className="event__stage-box">
        <h3>{getTranslatedField(stage, appLang, "title")}</h3>
        <div className="event__stage-info">
          <div className="event__item">
            <Icon name="clock" className="clock" />
            <span>
              {Array.isArray(stageInfo.date)
                ? (
                    labels?.EVENT_TIME_MULTIPLE_DAYS ||
                    "From [DATE_START] [TIME_START] to [DATE_END] [TIME_END]"
                  )
                    ?.replace("[DATE_START]", stageInfo?.dateStart)
                    ?.replace("[DATE_END]", stageInfo?.dateEnd)
                    ?.replace("[TIME_START]", stageInfo?.timeStart)
                    ?.replace("[TIME_END]", stageInfo?.timeEnd)
                : (labels?.EVENT_TIME || "From [TIME_START] to [TIME_END]")
                    ?.replace("[TIME_START]", stageInfo?.timeStart)
                    ?.replace("[TIME_END]", stageInfo?.timeEnd)}
            </span>
          </div>
          {stageInfo?.location && (
            <div className="event__item">
              <Icon name="location-pin" className="location-pin" />
              <span>{stageInfo?.location}</span>
            </div>
          )}
          {stage?.registrationsLimit > 0 && (
            <div className="event__item event__item--availability">
              {stage?.availableSeats === 0 ? (
                <>
                  <span className="event__sold-out">{labels?.EVENT_SOLD_OUT || "Sold out"}</span>
                  <span className="event__sold-out-info">
                    {labels?.EVENT_SOLD_OUT_INFO || "You can sign up to the waiting list"}
                  </span>
                </>
              ) : (
                !["REGISTERED", "QUEUED"]?.includes(stage?.status) && (
                  <>
                    <Icon name="users-light" className="users-light" />
                    <span>
                      {stage?.availableSeats === 1
                        ? (labels?.EVENT_AVAILABLE_SEAT || "[N] available seat").replace(
                            "[N]",
                            stage?.availableSeats
                          )
                        : (labels?.EVENT_AVAILABLE_SEATS || "[N] available seats").replace(
                            "[N]",
                            stage?.availableSeats
                          )}
                    </span>
                  </>
                )
              )}
            </div>
          )}
        </div>
        <div className="event__info-date">
          {(labels?.EVENT_REGISTRATIONS_CLOSE || "Registrations close on [DATE] at [TIME]")
            .replace(
              "[DATE]",
              dayjs(Number(stage?.registrationEnd)).locale(appLang?.value).format("LL")
            )
            .replace(
              "[TIME]",
              dayjs(Number(stage?.registrationEnd)).locale(appLang?.value).format("LT")
            )}
        </div>
        <button
          type="button"
          className="link link--accent event__cta"
          onClick={handleGoToStageDetail}
        >
          <span>{labels?.EVENT_MORE_DETAILS || "More details"}</span>
          <Icon name="angle-down" />
        </button>
      </div>
      {multiple && (
        <div className="event__select">
          <div
            className={classNames(
              "form__radio-wrapper form__radio-wrapper--border",
              Number(selectedStage) === stage?.id && "form__radio-wrapper--selected"
            )}
          >
            <input
              {...register("stage", { required: true })}
              type="radio"
              className="form__input form__input--radio"
              id={`radio-${stage?.id}`}
              value={stage?.id}
            />
            <label htmlFor={`radio-${stage?.id}`} className="form__radio-label">
              {labels?.EVENT_SELECT || "Select"}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageCard;
